import { h, Component } from 'preact';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';
import CSSTransition from 'react-transition-group/CSSTransition';
import { hot } from 'react-hot-loader';
import ReactHintFactory from 'react-hint';

import Text from '../../components/Global/Text';

// Components
import Section from '../../components/Section';
import InsuranceSection from '../../components/Section/InsuranceSection';
import Icon from '../../components/Global/Icon';
import Throbber from '../../components/Global/Throbber';

// Utils
import { getSectionById, hideProgressBar, hideBackButtonLink, hideCurrentProgressBar } from '../../utilities/section/sectionUtils';
import { stepHasRequiredData } from '../../utilities/form/formUtils';

// Actions
import { nextStepAction, prevStepAction } from '../../actions/Section';

const ReactHint = ReactHintFactory({ createElement: h, Component });

class App extends Component {
  constructor(props) {
    super(props);
    this.handleOnPopState = this.handleOnPopState.bind(this);
    this.setScrollPosition = this.setScrollPosition.bind(this);
    /* eslint-disable no-restricted-globals */
    if (history.state === null) {
      // Change due to IE bug and set the correct path to current location.
      history.replaceState(0, document.title, location);
      sessionStorage.setItem('stateLastShown', 0);
    }
    this.state = {
      previousHash: window.location.href.split('?')[1],
    };
    /* eslint-enable no-restricted-globals */
  }

  componentDidMount() {
    window.addEventListener('popstate', this.handleOnPopState);
  }

  componentDidUpdate() {
    // On route change, scroll to top
    const { previousHash } = this.state;
    const currentHash = window.location.href.split('?')[1];
    if (previousHash !== currentHash) {
      this.setScrollPosition();
    }
  }

  setScrollPosition() {
    const currentHash = window.location.href.split('?')[1];
    this.setState({
      previousHash: currentHash,
    });
    window.scroll({
      top: 0,
      behavior: 'smooth',
    });
  }

  handleOnPopState() {
    /* eslint-disable no-restricted-globals */
    const { state } = history;

    if (state !== null) {
      /* (-1, 0, 1) = (backward, do nothing, forward) */
      const position = Number(state);
      const stateLastShown = sessionStorage.getItem('stateLastShown');
      const positionLastShown = Number(stateLastShown);
      const direction = Math.sign(position - positionLastShown);

      // (2) Stamp the session with the last position shown
      sessionStorage.setItem('stateLastShown', state);

      const { actions } = this.props;
      // (3) Fire correct action.
      if (direction === -1) {
        actions.prevStepAction();
      }
      if (direction === 1) {
        actions.nextStepAction();
      }
    }
    /* eslint-enable no-restricted-globals */
  }

  render() {
    const {
      actions,
      appLoading,
      currentPageData,
      currentSectionId,
      dispatch,
      finishedSections,
      loaderProgress,
      packageData,
      price,
      sectionOrder,
      userData,
      error,
      errorData,
      debugServices,
      bodyClass,
    } = this.props;
    function hideBackButton(activeSectionData) {
      if (Object.hasOwnProperty.call(activeSectionData, 'hideBackButtonCondition')) {
        const { hideBackButtonCondition } = activeSectionData;
        const isValid = stepHasRequiredData(hideBackButtonCondition, userData, {});
        return isValid;
      }
      return activeSectionData.hideBackButton;
    }
    const activeSectionData = getSectionById(currentSectionId, packageData);
    const currentProgress = {
      currentSectionName: activeSectionData.name,
      currentSectionGroup: activeSectionData.sectionGroup,
      currentSectionType: activeSectionData.sectionType,
      hideProgress: hideProgressBar(
        activeSectionData.hideProgress,
        activeSectionData,
        currentPageData
      ),
      hideCurrentProgress: hideCurrentProgressBar(
        activeSectionData.hideCurrentProgress
      ),
      hideBackButton: hideBackButtonLink(
        activeSectionData.hideBackButton || hideBackButton(activeSectionData),
        activeSectionData,
        currentPageData
      ),
      backButtonConfig: activeSectionData.backButtonConfig,
      currentSectionId,
      currentPageData,
      finishedSections,
      sectionOrder,
    };

    // Refreshing and clearing
    const refresh = () => {
      localStorage.clear();
      location.reload(); // eslint-disable-line
    };
    const bodyClasses = `app ${bodyClass}`;

    return (
      <div className={bodyClasses}>

        {debugServices === true &&
          <button
            className="clear-localStorage"
            type="button"
            title="Clear localStorage"
            onClick={() => refresh()}
          >
            <Icon icon="info" />
          </button>
        }

        <ReactHint
          autoPosition
          events={{ hover: true }}
          onRenderContent={(target, content) =>
            <div className="react-hint__content">
              <Text value={content} numberFormat={true} component="span" />
            </div>}
        />

        {/* Throbber */}
        <Throbber
          appLoading={appLoading}
        />

        {packageData.sections && !currentProgress.currentSectionType &&
          <section className="sections">
            {packageData.sections.map(section => {
              const activeSection = section.sectionId === currentProgress.currentSectionId;
              if (!activeSection) {
                return <div />;
              }
              return (
                <CSSTransition
                  in={activeSection}
                  timeout={300}
                  classNames="slideInTop"
                  unmountOnExit
                >
                  <Section
                    actions={actions}
                    activeSectionData={section}
                    currentProgress={currentProgress}
                    currentSectionId={currentSectionId}
                    finishedSections={finishedSections}
                    loaderProgress={loaderProgress}
                    packageData={packageData}
                    price={price}
                    userData={userData}
                    sectionOrder={sectionOrder}
                    error={error}
                    errorData={errorData}
                    dispatch={dispatch}
                  />
                </CSSTransition>
              );
            })}
          </section>
        }
        {packageData.sections && currentProgress.currentSectionType &&
          <section className="sections">
            {packageData.sections.map(section => {
              const activeSection = section.sectionId === currentProgress.currentSectionId;
              if (!activeSection) {
                return <div />;
              }
              return (
                <CSSTransition
                  in={activeSection}
                  timeout={300}
                  classNames="slideInTop"
                  unmountOnExit
                >
                  <InsuranceSection
                    actions={actions}
                    activeSectionData={section}
                    currentProgress={currentProgress}
                    currentSectionId={currentSectionId}
                    finishedSections={finishedSections}
                    loaderProgress={loaderProgress}
                    packageData={packageData}
                    price={price}
                    userData={userData}
                    sectionOrder={sectionOrder}
                    error={error}
                    errorData={errorData}
                  />
                </CSSTransition>
              );
            })}
          </section>
        }
      </div>
    );
  }
}

export default compose(
  connect((state) => {
    const {
      userData,
      userData: {
        loaderProgress,
        price,
      },
      app: {
        appLoading,
        error,
        errorData,
        debugServices,
      },
      section: {
        currentSectionId,
        finishedSections,
        packageData,
        sectionOrder,
        currentPageData,
        bodyClass,
      },
    } = state;

    return {
      userData,
      price,
      loaderProgress,
      appLoading,
      currentPageData,
      currentSectionId,
      finishedSections,
      debugServices,
      error,
      errorData,
      packageData,
      sectionOrder,
      bodyClass,
    };
  }, (dispatch) => ({
    actions: bindActionCreators({
      prevStepAction,
      nextStepAction,
    }, dispatch),
    dispatch,
  }))
)(hot(module)(App));

App.defaultProps = {
  bodyClass: '',
};

App.propTypes = {
  appLoading: PropTypes.bool.isRequired,
  currentSectionId: PropTypes.number.isRequired,
  loaderProgress: PropTypes.number.isRequired,
  actions: PropTypes.object.isRequired,
  currentPageData: PropTypes.object.isRequired,
  debugServices: PropTypes.bool.isRequired,
  userData: PropTypes.object.isRequired,
  packageData: PropTypes.object.isRequired,
  finishedSections: PropTypes.array.isRequired,
  sectionOrder: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  price: PropTypes.string.isRequired,
  error: PropTypes.bool.isRequired,
  errorData: PropTypes.object.isRequired,
  bodyClass: PropTypes.string,
};



// WEBPACK FOOTER //
// ./src/containers/App/index.jsx